import { useEffect, useRef, useState, useCallback } from 'react';
import getBlobDuration from './blobToDuration';
import { isFiniteNumber } from '@core';

type UseAudioReturn = {
    audioRef: React.RefObject<HTMLAudioElement>;
    isPlaying: boolean;
    isMuted: boolean;
    duration: number;
    currentTime: number;
    progress: number;
    togglePlay: () => void;
    toggleMute: () => void;
    seek: (progressPercent: number) => void;
};

export function useAudio(src: string): UseAudioReturn {
    const audioRef = useRef<HTMLAudioElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoadedMetadata = async () => {
            const result = await getBlobDuration(audio.src);
            if (isFiniteNumber(result) && result > 0) {
                setDuration(result);
            } else if (Number.isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration);
            }
        };

        const onTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
            if (Number.isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration);
            }
        };

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);

        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            audio.currentTime = 0;
        };

        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);

        void onLoadedMetadata();

        return () => {
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
        };
    }, [src]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) void audio.play();
        else audio.pause();
    }, []);

    const toggleMute = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !audio.muted;
        setIsMuted(audio.muted || audio.volume === 0);
    }, []);

    const seek = useCallback(
        (progressPercent: number) => {
            const audio = audioRef.current;
            if (!audio || !duration) return;
            const clamped = Math.max(0, Math.min(100, progressPercent));
            audio.currentTime = (clamped / 100) * duration;
            setCurrentTime(audio.currentTime);
        },
        [duration],
    );

    const progress = duration ? (currentTime / duration) * 100 : 0;

    return {
        audioRef,
        isPlaying,
        isMuted,
        duration,
        currentTime,
        progress,
        togglePlay,
        toggleMute,
        seek,
    };
}
